import { useState, useCallback, useRef, useEffect } from "react";
import { abortBetChanges } from "../api/abortBetChanges";
import { declineBetChanges as declineBetChangesApi } from "../api/declineBetChanges";
import { PlaceBetStatus } from "../types/enums";
import { ExecuteOrder, PlaceBetSport } from "../api";
import { mapErrorToAction, ERROR_CODES } from "../shared/utils/error-handling";
import { useWidgetContext } from "../providers/WidgetProvider";
import { applyAuthorizationChanges } from "../shared/utils/bet-slip-authorization";
import { useAuthorizationPolling } from "./useAuthorizationPolling";

interface UsePlaceBetOptions {
  onPlaceBetSuccess?: (response: ExecuteOrderResponse) => void;
  onBookBetSuccess?: (
    bookingCode: string,
    response: PlaceBetSportResponse,
  ) => void;
  onError?: (error: string, errorCode?: number) => void;
  /** Called when betslip needs recovery. Call scheduleRetry() when acceptOddsChanges to auto-retry. */
  onRecoverBetSlip?: (
    additionalInfo: any[],
    scheduleRetry?: () => void,
  ) => void;
  /** Called when bonus rules changed (error 604). Typically triggers page reload. */
  onBonusRulesChanged?: () => void;
}

const IDLE_AUTH_STATE: AuthorizationState = {
  key: null,
  timeout: 0,
  secondsLeft: 0,
  totalSeconds: 0,
  status: PlaceBetStatus.Idle,
  message: null,
};

export const usePlaceBet = (options?: UsePlaceBetOptions) => {
  const { config, betSlipState, placeBetInProgressRef } = useWidgetContext();
  const [isLoading, setIsLoading] = useState(false);
  const [authState, setAuthState] = useState<AuthorizationState>(IDLE_AUTH_STATE);

  const currentRequestRef = useRef<PlaceBetSportPayload | null>(null);
  const maxTryCallsRef = useRef(0);
  const isBookingRef = useRef<boolean>(false);
  // Ref to break circular dep: useAuthorizationPolling.onAutoResubmit → executeBetInternal
  const executeBetInternalRef = useRef<
    | ((
        req: PlaceBetSportPayload,
        authKey?: string,
      ) => Promise<PlaceBetSportResponse | undefined>)
    | null
  >(null);

  const handleApiError = useCallback(
    (
      errorAction: ReturnType<typeof mapErrorToAction>,
      scheduleRetry?: () => void,
    ) => {
      const { action, message, additionalInfo } = errorAction;
      switch (action) {
        case "recover_betslip":
        case "event_specific_recover": {
          if (additionalInfo?.length && options?.onRecoverBetSlip) {
            const isRetriable = (ERROR_CODES.RETRIABLE_RECOVER as readonly number[]).includes(errorAction.errorCode);
            const doRecover = () =>
              options.onRecoverBetSlip!(additionalInfo, isRetriable ? scheduleRetry : undefined);
            action === "event_specific_recover"
              ? setTimeout(doRecover, 4000)
              : doRecover();
          }
          if (action === "event_specific_recover") {
            options?.onError?.(message, errorAction.errorCode);
          } else if (!options?.onRecoverBetSlip) {
            options?.onError?.(message, errorAction.errorCode);
          }
          break;
        }
        case "bonus_rules_changed":
          options?.onBonusRulesChanged?.();
          options?.onError?.(message, errorAction.errorCode);
          break;
        case "self_excluded":
          break;
        default:
          options?.onError?.(message, errorAction.errorCode);
      }
    },
    [options],
  );

  const { startPolling, stopPolling, startCountdown } = useAuthorizationPolling(
    {
      config,
      currentRequestRef,
      onAutoResubmit: (request, authKey) => {
        setAuthState((prev) => ({
          ...prev,
          key: null,
          timeout: 0,
          status: PlaceBetStatus.Placing,
          message: null,
        }));
        executeBetInternalRef.current?.(request, authKey);
      },
      onConfirmChanges: (message) => {
        setAuthState((prev) => ({
          ...prev,
          status: PlaceBetStatus.ConfirmChanges,
          message,
        }));
      },
      onDeclined: (message) => {
        setAuthState((prev) => ({
          ...prev,
          key: null,
          status: PlaceBetStatus.Declined,
          message,
        }));
        options?.onError?.("Bet was declined");
      },
      onExpired: () => {
        placeBetInProgressRef.current = false;
        setAuthState(IDLE_AUTH_STATE);
        options?.onError?.("PleaseTryAgainLater");
      },
      onCountdownTick: (secondsLeft) => {
        if (secondsLeft <= 0) {
          placeBetInProgressRef.current = false;
          setAuthState(IDLE_AUTH_STATE);
        } else {
          setAuthState((prev) => ({ ...prev, secondsLeft }));
        }
      },
      onWaiting: (newSecondsLeft, message) => {
        setAuthState((prev) => ({
          ...prev,
          status: PlaceBetStatus.Authorizing,
          secondsLeft: newSecondsLeft,
          message,
        }));
      },
      onError: (msg) => options?.onError?.(msg),
    },
  );

  const handleExecuteOrderResult = useCallback(
    (executeOrderResponse: ExecuteOrderResponse, scheduleRetry: () => void) => {
      if (executeOrderResponse.isSuccessfull) {
        maxTryCallsRef.current = 0;
        options?.onPlaceBetSuccess?.(executeOrderResponse);
      } else {
        const errorAction = mapErrorToAction(
          executeOrderResponse.result,
          "PleaseTryAgainLater",
        );
        handleApiError(errorAction, scheduleRetry);
      }
      placeBetInProgressRef.current = false;
      setAuthState(IDLE_AUTH_STATE);
      setIsLoading(false);
    },
    [options, handleApiError, placeBetInProgressRef],
  );

  const handleTimeoutGuid = useCallback(
    async (
      response: PlaceBetSportResponse,
      scheduleRetry: () => void,
    ): Promise<PlaceBetSportResponse> => {
      const timeoutMs = response.timeout || 0;
      if (timeoutMs > 0) {
        const totalSeconds = Math.ceil(timeoutMs / 1000);
        setAuthState((prev) => ({
          ...prev,
          status: PlaceBetStatus.Timeout,
          secondsLeft: totalSeconds,
          totalSeconds,
        }));
        await new Promise<void>((resolve) => {
          const tickInterval = setInterval(() => {
            setAuthState((prev) => ({
              ...prev,
              secondsLeft: Math.max(0, prev.secondsLeft - 1),
            }));
          }, 1000);
          setTimeout(() => {
            clearInterval(tickInterval);
            resolve();
          }, timeoutMs);
        });
      }
      try {
        const executeOrderResponse = await ExecuteOrder({
          timeoutGuid: response.timeoutGuid!,
          acceptOddsChanges: betSlipState.acceptOddsChanges,
          config: {
            brandId: config.brand.id,
            channelId: config.channelId,
            terminal: config.brand.terminal,
            language: config.culture,
            userToken: config.user.token,
          },
        });
        handleExecuteOrderResult(executeOrderResponse, scheduleRetry);
      } catch {
        placeBetInProgressRef.current = false;
        options?.onError?.("PleaseTryAgainLater");
        setAuthState(IDLE_AUTH_STATE);
        setIsLoading(false);
      }
      return response;
    },
    [betSlipState.acceptOddsChanges, config, options, handleExecuteOrderResult, placeBetInProgressRef],
  );

  const handleImmediateSuccess = useCallback(
    (
      response: PlaceBetSportResponse,
      request: PlaceBetSportPayload,
    ): PlaceBetSportResponse => {
      maxTryCallsRef.current = 0;
      placeBetInProgressRef.current = false;
      if (request.isBooking) {
        options?.onBookBetSuccess?.(response.number, response);
      }
      setAuthState(IDLE_AUTH_STATE);
      setIsLoading(false);
      return response;
    },
    [options, placeBetInProgressRef],
  );

  const handleAuthorizationRequired = useCallback(
    (response: PlaceBetSportResponse): PlaceBetSportResponse => {
      const totalSecs = Math.floor(response.authorizationTimeout / 1000);
      setAuthState({
        key: response.authorizationKey!,
        timeout: response.authorizationTimeout,
        secondsLeft: totalSecs,
        totalSeconds: totalSecs,
        status: PlaceBetStatus.Authorizing,
        message: null,
      });
      startCountdown(response.authorizationTimeout || 60000);
      startPolling(response.authorizationKey!);
      setIsLoading(false);
      return response;
    },
    [startCountdown, startPolling],
  );

  const executeBetInternal = useCallback(
    async (
      request: PlaceBetSportPayload,
      authorizationKey?: string,
    ): Promise<PlaceBetSportResponse | undefined> => {
      const scheduleRetry = () => {
        const maxTry = config.brand.maxTryCalls ?? 5;
        const delay = config.brand.delayCall ?? 500;
        if (maxTryCallsRef.current < maxTry && currentRequestRef.current) {
          maxTryCallsRef.current++;
          setTimeout(
            () => executeBetInternalRef.current?.(currentRequestRef.current!),
            delay,
          );
        } else {
          placeBetInProgressRef.current = false;
          setAuthState(IDLE_AUTH_STATE);
          setIsLoading(false);
          options?.onError?.("ChangesInBetslipTryAgain");
        }
      };
      try {
        const requestWithAuth = authorizationKey
          ? { ...request, authorizationKey }
          : request;
        const response = await PlaceBetSport(requestWithAuth);

        if (!request.isBooking && response.isSuccessfull && response.timeoutGuid) {
          return handleTimeoutGuid(response, scheduleRetry);
        }
        if (
          (response.isSuccessfull && response.placedSuccessfully) ||
          (request.isBooking && response.isSuccessfull)
        ) {
          return handleImmediateSuccess(response, request);
        }
        if (
          response.authorizationKey &&
          response.authorizationTimeout > 0 &&
          [270, 271].indexOf(response.result?.errorCode) > -1
        ) {
          return handleAuthorizationRequired(response);
        }
        const errorAction = mapErrorToAction(
          response.result,
          "PleaseTryAgainLater",
        );
        placeBetInProgressRef.current = false;
        handleApiError(errorAction, scheduleRetry);
        setAuthState(IDLE_AUTH_STATE);
        setIsLoading(false);
        return response;
      } catch {
        placeBetInProgressRef.current = false;
        options?.onError?.("PleaseTryAgainLater");
        setAuthState(IDLE_AUTH_STATE);
        setIsLoading(false);
      }
    },
    [
      options,
      config,
      handleApiError,
      handleTimeoutGuid,
      handleImmediateSuccess,
      handleAuthorizationRequired,
      placeBetInProgressRef,
    ],
  );

  // Keep ref in sync so polling callbacks can reach the latest version
  useEffect(() => {
    executeBetInternalRef.current = executeBetInternal;
  }, [executeBetInternal]);

  const executeBet = useCallback(
    async (request: PlaceBetSportPayload, authorizationKey?: string) => {
      maxTryCallsRef.current = 0;
      placeBetInProgressRef.current = true;
      setIsLoading(true);
      isBookingRef.current = request.isBooking;
      currentRequestRef.current = request;
      setAuthState((prev) => ({ ...prev, status: PlaceBetStatus.Placing }));
      return executeBetInternal(request, authorizationKey);
    },
    [executeBetInternal, placeBetInProgressRef],
  );

  const abortBet = useCallback(async () => {
    if (!authState.key) return;
    try {
      await abortBetChanges({
        authorizationKey: authState.key,
        secondsLeft: authState.secondsLeft,
        config: {
          brandId: config.brand.id,
          channelId: config.channelId,
          language: config.culture,
          userToken: config.user.token,
        },
      });
    } catch {
      console.error("Error aborting bet");
    } finally {
      placeBetInProgressRef.current = false;
      stopPolling();
      setAuthState(IDLE_AUTH_STATE);
      setIsLoading(false);
    }
  }, [authState.key, authState.secondsLeft, stopPolling, config, placeBetInProgressRef]);

  const declineBetChanges = useCallback(async () => {
    // Unblock odds updates immediately regardless of whether the API call succeeds.
    placeBetInProgressRef.current = false;
    if (!authState.message?.authorizationKey) return;
    try {
      await declineBetChangesApi({
        authorizationKey: authState.message.authorizationKey,
        config: {
          brandId: config.brand.id,
          channelId: config.channelId,
          language: config.culture,
          userToken: config.user.token,
        },
      });
    } catch {
      console.error("Error declining bet");
    } finally {
      stopPolling();
      setAuthState(IDLE_AUTH_STATE);
      setIsLoading(false);
    }
  }, [authState.message, stopPolling, config, placeBetInProgressRef]);

  const confirmBetChanges = useCallback(async () => {
    if (!authState.message?.authorizationKey || !currentRequestRef.current)
      return;
    const request = applyAuthorizationChanges(
      currentRequestRef.current,
      authState.message,
    );
    setIsLoading(true);
    setAuthState((prev) => ({ ...prev, status: PlaceBetStatus.Placing }));
    await executeBetInternal(request, authState.message.authorizationKey);
  }, [authState.message, executeBetInternal]);

  const clearAuthState = useCallback(() => {
    placeBetInProgressRef.current = false;
    stopPolling();
    setAuthState(IDLE_AUTH_STATE);
    setIsLoading(false);
  }, [stopPolling, placeBetInProgressRef]);

  return {
    executeBet,
    abortBet,
    declineBetChanges,
    confirmBetChanges,
    clearAuthState,
    authState,
    isLoading,
    isBookingLoading: isLoading && isBookingRef.current,
  };
};
